import React, { useState, useEffect, useRef } from 'react';
import { X, Send, User, Building2, Loader2, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DocumentNote, User as UserType } from '../types';
import { api } from '../services/api';
import { formatDateTime } from '../utils/formatters';

interface DocumentNotesModalProps {
    isOpen: boolean;
    onClose: () => void;
    documentId: string;
    documentTitle: string;
    currentUser: UserType;
    agencyName?: string; // New Prop
}

export const DocumentNotesModal: React.FC<DocumentNotesModalProps> = ({
    isOpen,
    onClose,
    documentId,
    documentTitle,
    currentUser,
    agencyName
}) => {
    const [notes, setNotes] = useState<DocumentNote[]>([]);
    const [newNote, setNewNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation('common');

    // Scroll to bottom when notes change
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isOpen && documentId) {
            loadNotes();
        }
    }, [isOpen, documentId]);

    useEffect(() => {
        scrollToBottom();
    }, [notes]);

    const loadNotes = async () => {
        setLoading(true);
        try {
            const data = await api.getDocumentNotes(documentId);
            setNotes(data);
        } catch (error) {
            console.error("Failed to load notes", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!newNote.trim()) return;

        setSending(true);
        try {
            // Determine Names
            let finalUserName = currentUser.profile?.companyName || currentUser.config?.companyName || currentUser.email;
            let finalMemberName: string | null = null; // Changed from undefined to null for Firestore compatibility

            if (currentUser.role === 'agency_member') {
                finalUserName = agencyName || 'Agency'; // Force Agency Name
                finalMemberName = currentUser.memberProfile?.name || currentUser.profile?.responsiblePerson || 'Team Member';
            } else if (currentUser.role === 'agency') {
                finalUserName = agencyName || currentUser.profile?.companyName || 'Agency';
            }

            const noteData = {
                documentId,
                userId: currentUser.id,
                userName: finalUserName,
                content: newNote.trim(),
                createdAt: new Date().toISOString(),
                isAgency: currentUser.role === 'agency' || currentUser.role === 'agency_member' || currentUser.role === 'admin',
                memberName: finalMemberName
            };

            await api.addDocumentNote(documentId, noteData);
            setNewNote('');
            loadNotes();
        } catch (error) {
            console.error("Failed to send note", error);
            alert("Failed to send message");
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fadeIn backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh] overflow-hidden border border-slate-200">

                {/* Header */}
                <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                    <div>
                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            <MessageSquare size={18} className="text-yellow-500" />
                            Notes
                        </h3>
                        <p className="text-xs text-slate-500 truncate max-w-[300px]">{documentTitle}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-500 transition">
                        <X size={20} />
                    </button>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
                    {loading ? (
                        <div className="flex justify-center items-center h-40">
                            <Loader2 className="animate-spin text-slate-400" size={24} />
                        </div>
                    ) : notes.length === 0 ? (
                        <div className="text-center text-slate-400 py-10">
                            <MessageSquare size={32} className="mx-auto mb-2 opacity-20" />
                            <p className="text-sm">No notes yet. Start the discussion!</p>
                        </div>
                    ) : (
                        notes.map((note) => {
                            const isMe = note.userId === currentUser.id;
                            const isAgency = note.isAgency;

                            return (
                                <div key={note.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[80%] rounded-2xl p-3 shadow-sm ${isMe
                                        ? 'bg-blue-600 text-white rounded-br-none'
                                        : 'bg-white border border-slate-100 text-slate-800 rounded-bl-none'
                                        }`}>
                                        <div className="flex items-center gap-2 mb-1 opacity-80">
                                            {isAgency ? <Building2 size={12} /> : <User size={12} />}
                                            <div className="flex flex-col leading-tight">
                                                <span className="text-[10px] font-bold uppercase tracking-wider">{note.userName}</span>
                                                {note.memberName && (
                                                    <span className="text-[9px] font-medium opacity-90">{note.memberName}</span>
                                                )}
                                            </div>
                                            <span className="text-[10px] ml-auto self-start pl-2">{formatDateTime(note.createdAt)}</span>
                                        </div>
                                        <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                                    </div>
                                </div>
                            );
                        })
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 bg-white border-t border-slate-100">
                    <form onSubmit={handleSend} className="flex gap-2">
                        <input
                            type="text"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Type a note..."
                            className="flex-1 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50"
                            autoFocus
                        />
                        <button
                            type="submit"
                            disabled={sending || !newNote.trim()}
                            className="bg-blue-600 text-white p-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg shadow-blue-200"
                        >
                            {sending ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};
